<template>
  <div class="login-register-page">
    <!-- Toast 提示容器（支持多条） -->
    <div class="toast-container" aria-live="polite">
      <transition-group name="toast-list">
        <div
          v-for="toast in toastList"
          :key="toast.id"
          :class="['toast-item', `toast-${toast.type}`]"
          role="status"
        >
          <div class="toast-icon" aria-hidden="true">
            <span v-if="toast.type === 'success'">✓</span>
            <span v-else-if="toast.type === 'error'">✕</span>
            <span v-else-if="toast.type === 'warning'">!</span>
            <span v-else>ⓘ</span>
          </div>
          <span class="toast-message">{{ toast.message }}</span>
          <button
            class="toast-close"
            @click="removeToast(toast.id)"
            aria-label="关闭提示"
            type="button"
          >×</button>
          <div
            class="toast-progress"
            :style="{ width: toast.progress + '%' }"
          ></div>
        </div>
      </transition-group>
    </div>

    <!-- 左侧品牌区 -->
    <div class="left-side">
      <div class="brand-section">
        <img
          :src="defaultAvatarUrl"
          alt="品牌标识"
          class="brand-logo"
        />
        <h1 class="brand-title">欢迎回来</h1>
        <p class="brand-subtitle">登录以继续您的旅程</p>
      </div>
    </div>

    <!-- 右侧表单区 -->
    <div class="right-side">
      <div v-if="!isLoggedIn" class="login-register-box">
        <!-- Tab 切换 -->
        <div class="toggle-buttons" role="tablist">
          <button
            @click="switchTab('login')"
            :class="{ active: activeTab === 'login' }"
            role="tab"
            :aria-selected="activeTab === 'login'"
            :aria-controls="'panel-' + activeTab"
            :tabindex="activeTab === 'login' ? 0 : -1"
            @keyup.enter="switchTab('login')"
            @keyup.space.prevent="switchTab('login')"
          >登录</button>
          <button
            @click="switchTab('register')"
            :class="{ active: activeTab === 'register' }"
            role="tab"
            :aria-selected="activeTab === 'register'"
            :aria-controls="'panel-' + activeTab"
            :tabindex="activeTab === 'register' ? 0 : -1"
            @keyup.enter="switchTab('register')"
            @keyup.space.prevent="switchTab('register')"
          >注册</button>
        </div>

        <div :id="'panel-' + activeTab" class="form-content" role="tabpanel">
          <!-- 手机号（公用） -->
          <div class="input-group">
            <label for="phone-input" class="sr-only">手机号码</label>
            <div class="phone-input">
              <label for="country-select" class="sr-only">国家/地区代码</label>
              <select
                id="country-select"
                v-model="selectedCountryCode"
                class="country-select"
                aria-label="选择国家/地区代码"
              >
                <option value="+86">🇨🇳 +86</option>
                <option value="+852">🇭🇰 +852</option>
                <option value="+853">🇲🇴 +853</option>
                <option value="+886">🇹🇼 +886</option>
              </select>
              <div class="phone-number-wrapper">
                <input
                  ref="phoneInput"
                  id="phone-input"
                  type="tel"
                  inputmode="numeric"
                  v-model="phoneNumber"
                  placeholder="请输入11位手机号"
                  maxlength="11"
                  @input="handlePhoneInput"
                  @blur="validatePhoneNumber"
                  :class="{ error: phoneNumberError }"
                  aria-invalid="!!phoneNumberError"
                  aria-describedby="phone-error"
                  autocomplete="tel"
                />
                <button
                  v-if="phoneNumber"
                  class="input-clear-btn"
                  @click="clearPhoneNumber"
                  aria-label="清除手机号"
                  type="button"
                >✕</button>
              </div>
            </div>
            <div
              id="phone-error"
              class="error-message"
              v-if="phoneNumberError"
              role="alert"
            >{{ phoneNumberError }}</div>
          </div>

          <!-- ==================== 登录流程 ==================== -->
          <div v-if="activeTab === 'login'">
            <!-- 密码登录 -->
            <div v-if="loginMethod === 'password'">
              <div class="input-group">
                <label for="password-input" class="sr-only">登录密码</label>
                <div class="password-input-wrapper">
                  <input
                    ref="passwordInput"
                    id="password-input"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="password"
                    placeholder="请输入6位密码（字母/数字）"
                    maxlength="6"
                    @input="handlePasswordInput"
                    @blur="validatePassword"
                    :class="{ error: passwordError }"
                    aria-invalid="!!passwordError"
                    aria-describedby="password-error"
                    autocomplete="current-password"
                  />
                  <button
                    type="button"
                    class="toggle-visibility"
                    @click="showPassword = !showPassword"
                    :aria-label="showPassword ? '隐藏密码' : '显示密码'"
                  >
                    <svg v-if="!showPassword" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  </button>
                </div>
                <div
                  id="password-error"
                  class="error-message"
                  v-if="passwordError"
                  role="alert"
                >{{ passwordError }}</div>
                <div
                  v-if="password && !passwordError && password.length === 6"
                  class="password-strength"
                >
                  <span class="strength-label">密码强度：</span>
                  <div class="strength-bar">
                    <div
                      :class="['strength-fill', 'strength-' + passwordStrength]"
                      :style="{ width: strengthPercent + '%' }"
                    ></div>
                  </div>
                  <span :class="['strength-text', 'text-' + passwordStrength]">
                    {{ passwordStrengthText }}
                  </span>
                </div>
              </div>

              <div class="remember-me">
                <label class="checkbox-label-wrapper">
                  <input
                    type="checkbox"
                    v-model="rememberMe"
                    id="rememberMe"
                  />
                  <span class="checkbox-text">记住登录状态</span>
                </label>
              </div>

              <button
                class="main-button"
                @click="handleLogin"
                :disabled="isLoading || !isPasswordLoginFormValid"
                :aria-busy="isLoading"
                type="button"
              >
                <span v-if="isLoading" class="loading-spinner" aria-hidden="true"></span>
                <span>{{ isLoading ? '登录中...' : '登录' }}</span>
              </button>

              <div class="additional-options">
                <span
                  class="link-text"
                  @click="switchLoginMethod('sms')"
                  @keyup.enter="switchLoginMethod('sms')"
                  tabindex="0"
                  role="button"
                >短信登录</span>
                <span
                  class="link-text"
                  @click="switchLoginMethod('forgot')"
                  @keyup.enter="switchLoginMethod('forgot')"
                  tabindex="0"
                  role="button"
                >忘记密码</span>
              </div>
            </div>

            <!-- 短信登录 -->
            <div v-else-if="loginMethod === 'sms'">
              <div class="input-group">
                <label for="sms-code-input" class="sr-only">短信验证码</label>
                <div class="verification-code">
                  <input
                    ref="smsCodeInput"
                    id="sms-code-input"
                    type="text"
                    inputmode="numeric"
                    v-model="verificationCode"
                    placeholder="请输入6位验证码"
                    maxlength="6"
                    @input="handleVerificationCodeInput"
                    @blur="validateVerificationCode"
                    :class="{ error: verificationCodeError }"
                    aria-invalid="!!verificationCodeError"
                    aria-describedby="sms-code-error"
                    autocomplete="new-password"
                    :name="'sms-code-' + Date.now()"
                    data-lpignore="true"
                    readonly
                    @focus="e => e.target.removeAttribute('readonly')"
                  />
                  <button
                    class="code-button"
                    @click="sendVerificationCode('sms')"
                    :disabled="isCodeButtonDisabled"
                    type="button"
                  >{{ getCodeButtonText }}</button>
                </div>
                <div
                  id="sms-code-error"
                  class="error-message"
                  v-if="verificationCodeError"
                  role="alert"
                >{{ verificationCodeError }}</div>
                <div
                  v-if="codeExpiryCountdown > 0"
                  class="code-expiry-hint"
                  aria-live="polite"
                >⏱ 验证码剩余：{{ formatExpiryTime }}</div>
              </div>

              <button
                class="main-button"
                @click="handleSmsLogin"
                :disabled="isLoading || !isSmsLoginFormValid"
                :aria-busy="isLoading"
                type="button"
              >
                <span v-if="isLoading" class="loading-spinner" aria-hidden="true"></span>
                <span>{{ isLoading ? '登录中...' : '登录' }}</span>
              </button>

              <div class="additional-options">
                <span
                  class="link-text"
                  @click="switchLoginMethod('password')"
                  @keyup.enter="switchLoginMethod('password')"
                  tabindex="0"
                  role="button"
                >← 返回密码登录</span>
              </div>
            </div>

            <!-- 忘记密码 -->
            <div v-else-if="loginMethod === 'forgot'">
              <div class="input-group">
                <label for="forgot-code-input" class="sr-only">验证码</label>
                <div class="verification-code">
                  <input
                    ref="forgotCodeInput"
                    id="forgot-code-input"
                    type="text"
                    inputmode="numeric"
                    v-model="verificationCode"
                    placeholder="请输入6位验证码"
                    maxlength="6"
                    @input="handleVerificationCodeInput"
                    @blur="validateVerificationCode"
                    :class="{ error: verificationCodeError }"
                    aria-invalid="!!verificationCodeError"
                    aria-describedby="forgot-code-error"
                    autocomplete="new-password"
                    :name="'forgot-code-' + Date.now()"
                    data-lpignore="true"
                    readonly
                    @focus="e => e.target.removeAttribute('readonly')"
                  />
                  <button
                    class="code-button"
                    @click="sendVerificationCode('forgot')"
                    :disabled="isCodeButtonDisabled"
                    type="button"
                  >{{ getCodeButtonText }}</button>
                </div>
                <div
                  id="forgot-code-error"
                  class="error-message"
                  v-if="verificationCodeError"
                  role="alert"
                >{{ verificationCodeError }}</div>
                <div
                  v-if="codeExpiryCountdown > 0"
                  class="code-expiry-hint"
                >⏱ 验证码剩余：{{ formatExpiryTime }}</div>
              </div>

              <div class="input-group">
                <label for="new-password-input" class="sr-only">新密码</label>
                <div class="password-input-wrapper">
                  <input
                    ref="newPasswordInput"
                    id="new-password-input"
                    :type="showNewPassword ? 'text' : 'password'"
                    v-model="newPassword"
                    placeholder="请输入6位新密码（字母/数字）"
                    maxlength="6"
                    @input="handleNewPasswordInput"
                    @blur="validateNewPassword"
                    :class="{ error: newPasswordError }"
                    aria-invalid="!!newPasswordError"
                    aria-describedby="new-password-error"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="toggle-visibility"
                    @click="showNewPassword = !showNewPassword"
                    :aria-label="showNewPassword ? '隐藏密码' : '显示密码'"
                  >
                    <svg v-if="!showNewPassword" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  </button>
                </div>
                <div
                  id="new-password-error"
                  class="error-message"
                  v-if="newPasswordError"
                  role="alert"
                >{{ newPasswordError }}</div>
                <small class="input-hint">密码需6位，可为数字、字母或组合</small>
              </div>

              <button
                class="main-button"
                @click="handleForgotPasswordLogin"
                :disabled="isLoading || !isForgotPasswordFormValid"
                :aria-busy="isLoading"
                type="button"
              >
                <span v-if="isLoading" class="loading-spinner" aria-hidden="true"></span>
                <span>{{ isLoading ? '重置中...' : '重置密码并登录' }}</span>
              </button>

              <div class="back-to-login">
                <span
                  class="link-text"
                  @click="switchLoginMethod('password')"
                  @keyup.enter="switchLoginMethod('password')"
                  tabindex="0"
                  role="button"
                >← 返回登录</span>
              </div>
            </div>
          </div>

          <!-- ==================== 注册流程 ==================== -->
          <div v-else-if="activeTab === 'register'">
            <div class="input-group">
              <label for="register-code-input" class="sr-only">验证码</label>
              <div class="verification-code">
                <input
                  ref="registerCodeInput"
                  id="register-code-input"
                  type="text"
                  inputmode="numeric"
                  v-model="verificationCode"
                  placeholder="请输入6位验证码"
                  maxlength="6"
                  @input="handleVerificationCodeInput"
                  @blur="validateVerificationCode"
                  :class="{ error: verificationCodeError }"
                  aria-invalid="!!verificationCodeError"
                  aria-describedby="register-code-error"
                  autocomplete="new-password"
                  :name="'register-code-' + Date.now()"
                  data-lpignore="true"
                  readonly
                  @focus="e => e.target.removeAttribute('readonly')"
                />
                <button
                  class="code-button"
                  @click="sendVerificationCode('register')"
                  :disabled="isCodeButtonDisabled"
                  type="button"
                >{{ getCodeButtonText }}</button>
              </div>
              <div
                id="register-code-error"
                class="error-message"
                v-if="verificationCodeError"
                role="alert"
              >{{ verificationCodeError }}</div>
              <div
                v-if="codeExpiryCountdown > 0"
                class="code-expiry-hint"
              >⏱ 验证码剩余：{{ formatExpiryTime }}</div>
            </div>

            <div class="input-group">
              <label for="register-password-input" class="sr-only">设置密码</label>
              <div class="password-input-wrapper">
                <input
                  ref="registerPasswordInput"
                  id="register-password-input"
                  :type="showRegisterPassword ? 'text' : 'password'"
                  v-model="newPassword"
                  placeholder="请输入6位密码（字母/数字）"
                  maxlength="6"
                  @input="handleNewPasswordInput"
                  @blur="validateNewPassword"
                  :class="{ error: newPasswordError }"
                  aria-invalid="!!newPasswordError"
                  aria-describedby="register-password-error"
                  autocomplete="new-password"
                />
                <button
                  type="button"
                  class="toggle-visibility"
                  @click="showRegisterPassword = !showRegisterPassword"
                  :aria-label="showRegisterPassword ? '隐藏密码' : '显示密码'"
                >
                  <svg v-if="!showRegisterPassword" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                </button>
              </div>
              <div
                id="register-password-error"
                class="error-message"
                v-if="newPasswordError"
                role="alert"
              >{{ newPasswordError }}</div>
              <small class="input-hint">密码需6位，可为数字、字母或组合</small>
            </div>

            <div class="agreement">
              <label class="checkbox-container">
                <input
                  type="checkbox"
                  v-model="agreementChecked"
                  id="agreement-checkbox"
                />
                <span class="custom-checkmark" aria-hidden="true"></span>
              </label>
              <span class="agreement-text">
                我已阅读并同意
                <span class="link-text" @click="goToUserAgreement" tabindex="0" role="link">《用户服务协议》</span>和
                <span class="link-text" @click="goToPrivacyPolicy" tabindex="0" role="link">《隐私协议》</span>
              </span>
            </div>

            <button
              class="main-button"
              @click="handleRegister"
              :disabled="isRegistering || !isRegisterFormValid"
              :aria-busy="isRegistering"
              type="button"
            >
              <span v-if="isRegistering" class="loading-spinner" aria-hidden="true"></span>
              <span>{{ isRegistering ? '注册中...' : '注册' }}</span>
            </button>

            <p class="login-link">
              已有账号，
              <span
                class="link-text"
                @click="switchTab('login')"
                @keyup.enter="switchTab('login')"
                tabindex="0"
                role="button"
              >点击登录</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { registerUser, forgotPassword, sendVerificationCodeApi, smsLogin, passwordLogin } from '@/api/upfile'
import { defAvatarUrl } from '@/api/constants'

export default {
  name: 'LoginRegisterPage',
  data () {
    return {
      defaultAvatarUrl: defAvatarUrl,
      activeTab: 'login',
      loginMethod: 'password',
      selectedCountryCode: '+86',
      phoneNumber: '',
      password: '',
      newPassword: '',
      verificationCode: '',
      agreementChecked: false,
      rememberMe: false,
      showPassword: false,
      showNewPassword: false,
      showRegisterPassword: false,
      isLoading: false,
      isCodeSending: false,
      isRegistering: false,
      phoneNumberError: '',
      passwordError: '',
      newPasswordError: '',
      verificationCodeError: '',
      passwordStrength: '',
      countdown: 0,
      sendCodeAttempts: 0,
      maxSendCodeAttempts: 5,
      verificationCodeExpiry: null,
      codeExpiryCountdown: 0,
      countdownTimer: null,
      codeExpiryTimer: null,
      phoneDebounceTimer: null,
      passwordDebounceTimer: null,
      newPasswordDebounceTimer: null,
      codeDebounceTimer: null,
      toastList: [],
      toastIdCounter: 0
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn']),
    ...mapState(['user']),
    isCodeButtonDisabled () {
      return (
        this.isCodeSending ||
        this.countdown > 0 ||
        !this.phoneNumber ||
        this.phoneNumber.length < 11 ||
        this.phoneNumberError !== ''
      )
    },
    isPasswordLoginFormValid () {
      return (
        this.phoneNumber &&
        this.password &&
        this.phoneNumber.length === 11 &&
        this.password.length === 6 &&
        !this.phoneNumberError &&
        !this.passwordError
      )
    },
    isSmsLoginFormValid () {
      return (
        this.phoneNumber &&
        this.verificationCode &&
        this.phoneNumber.length === 11 &&
        this.verificationCode.length === 6 &&
        !this.phoneNumberError &&
        !this.verificationCodeError
      )
    },
    isForgotPasswordFormValid () {
      return (
        this.phoneNumber &&
        this.verificationCode &&
        this.newPassword &&
        this.phoneNumber.length === 11 &&
        this.verificationCode.length === 6 &&
        this.newPassword.length === 6 &&
        !this.phoneNumberError &&
        !this.verificationCodeError &&
        !this.newPasswordError
      )
    },
    isRegisterFormValid () {
      return (
        this.phoneNumber &&
        this.verificationCode &&
        this.newPassword &&
        this.agreementChecked &&
        this.phoneNumber.length === 11 &&
        this.verificationCode.length === 6 &&
        this.newPassword.length === 6 &&
        !this.phoneNumberError &&
        !this.verificationCodeError &&
        !this.newPasswordError
      )
    },
    getCodeButtonText () {
      if (this.isCodeSending) return '发送中...'
      if (this.countdown > 0) return `${this.countdown}s 后重发`
      return '获取验证码'
    },
    passwordStrengthText () {
      const map = { weak: '弱', medium: '中', strong: '强' }
      return map[this.passwordStrength] || ''
    },
    strengthPercent () {
      const map = { weak: 33, medium: 66, strong: 100 }
      return map[this.passwordStrength] || 0
    },
    formatExpiryTime () {
      if (this.codeExpiryCountdown <= 0) return '00:00'
      const mins = Math.floor(this.codeExpiryCountdown / 60)
      const secs = this.codeExpiryCountdown % 60
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    }
  },
  methods: {
    ...mapActions(['login', 'logout', 'checkAuth']),

    showToast (message, type = 'info', duration = 3500) {
      const id = ++this.toastIdCounter
      const toast = {
        id,
        message,
        type,
        duration,
        progress: 100,
        startTime: Date.now(),
        timer: null,
        interval: null
      }
      this.toastList.push(toast)
      toast.timer = setTimeout(() => {
        this.removeToast(id)
      }, duration)
      toast.interval = setInterval(() => {
        const elapsed = Date.now() - toast.startTime
        const prog = Math.max(0, 100 - (elapsed / duration) * 100)
        const idx = this.toastList.findIndex(t => t.id === id)
        if (idx !== -1) {
          this.$set(this.toastList[idx], 'progress', prog)
        }
        if (prog <= 0) {
          this.removeToast(id)
        }
      }, 80)
    },
    removeToast (id) {
      const idx = this.toastList.findIndex(t => t.id === id)
      if (idx !== -1) {
        const toast = this.toastList[idx]
        if (toast.timer) clearTimeout(toast.timer)
        if (toast.interval) clearInterval(toast.interval)
        this.toastList.splice(idx, 1)
      }
    },

    switchTab (tab) {
      if (this.activeTab === tab) return
      this.activeTab = tab
      this.clearFormErrors()
      this.password = ''
      this.newPassword = ''
      this.verificationCode = ''
      this.agreementChecked = false
      this.passwordStrength = ''
      if (tab === 'login') {
        this.loginMethod = 'password'
      }
      this.resetTimers()
      this.resetValidationStates()
      this.$nextTick(() => {
        this.$refs.phoneInput?.focus()
      })
    },
    switchLoginMethod (method) {
      if (this.loginMethod === method) return
      this.loginMethod = method
      this.clearFormErrors()
      this.password = ''
      this.verificationCode = ''
      this.newPassword = ''
      this.resetTimers()
      this.resetValidationStates()
      this.$nextTick(() => {
        if (method === 'password') this.$refs.passwordInput?.focus()
        else if (method === 'sms') this.$refs.smsCodeInput?.focus()
        else if (method === 'forgot') this.$refs.forgotCodeInput?.focus()
      })
    },
    clearFormErrors () {
      this.phoneNumberError = ''
      this.passwordError = ''
      this.newPasswordError = ''
      this.verificationCodeError = ''
    },

    handlePhoneInput () {
      this.phoneNumber = this.phoneNumber.replace(/\D/g, '')
      if (this.phoneNumber.length === 11) {
        this.phoneNumberError = ''
      }
      clearTimeout(this.phoneDebounceTimer)
      this.phoneDebounceTimer = setTimeout(() => {
        this.validatePhoneNumber()
      }, 300)
    },
    clearPhoneNumber () {
      this.phoneNumber = ''
      this.phoneNumberError = ''
      this.$refs.phoneInput?.focus()
    },
    handlePasswordInput () {
      clearTimeout(this.passwordDebounceTimer)
      this.passwordDebounceTimer = setTimeout(() => {
        this.validatePassword()
      }, 300)
    },
    handleNewPasswordInput () {
      clearTimeout(this.newPasswordDebounceTimer)
      this.newPasswordDebounceTimer = setTimeout(() => {
        this.validateNewPassword()
      }, 300)
    },
    handleVerificationCodeInput () {
      this.verificationCode = this.verificationCode.replace(/\D/g, '')
      clearTimeout(this.codeDebounceTimer)
      this.codeDebounceTimer = setTimeout(() => {
        this.validateVerificationCode()
      }, 300)
    },

    validatePhoneNumber () {
      if (!this.phoneNumber) {
        this.phoneNumberError = ''
        return false
      }
      const phoneRegex = /^1[3-9]\d{9}$/
      if (!phoneRegex.test(this.phoneNumber)) {
        this.phoneNumberError = '请输入有效的11位手机号码'
        return false
      }
      this.phoneNumberError = ''
      return true
    },
    validatePassword () {
      if (!this.password) {
        this.passwordError = ''
        this.passwordStrength = ''
        return false
      }
      if (this.password.length !== 6) {
        this.passwordError = '密码长度必须恰好为6位'
        this.passwordStrength = ''
        return false
      }
      if (!/^[a-zA-Z0-9]{6}$/.test(this.password)) {
        this.passwordError = '密码只能包含字母或数字'
        this.passwordStrength = ''
        return false
      }
      let score = 0
      if (/[a-z]/.test(this.password)) score++
      if (/[A-Z]/.test(this.password)) score++
      if (/[0-9]/.test(this.password)) score++
      if (score <= 1) this.passwordStrength = 'weak'
      else if (score === 2) this.passwordStrength = 'medium'
      else this.passwordStrength = 'strong'
      this.passwordError = ''
      return true
    },
    validateNewPassword () {
      if (!this.newPassword) {
        this.newPasswordError = ''
        return false
      }
      if (this.newPassword.length !== 6) {
        this.newPasswordError = '密码长度必须恰好为6位'
        return false
      }
      if (!/^[a-zA-Z0-9]{6}$/.test(this.newPassword)) {
        this.newPasswordError = '密码只能包含字母或数字'
        return false
      }
      this.newPasswordError = ''
      return true
    },
    validateVerificationCode () {
      if (!this.verificationCode) {
        this.verificationCodeError = ''
        return false
      }
      if (!/^\d{6}$/.test(this.verificationCode)) {
        this.verificationCodeError = '验证码必须是6位数字'
        return false
      }
      if (this.verificationCodeExpiry && Date.now() > this.verificationCodeExpiry) {
        this.verificationCodeError = '验证码已过期，请重新获取'
        return false
      }
      this.verificationCodeError = ''
      return true
    },

    focusFirstError () {
      this.$nextTick(() => {
        const errorFields = [
          { error: this.phoneNumberError, ref: this.$refs.phoneInput },
          { error: this.passwordError, ref: this.$refs.passwordInput },
          { error: this.newPasswordError, ref: this.$refs.newPasswordInput || this.$refs.registerPasswordInput },
          { error: this.verificationCodeError, ref: this.$refs.smsCodeInput || this.$refs.forgotCodeInput || this.$refs.registerCodeInput }
        ]
        const firstError = errorFields.find(field => field.error && field.ref)
        if (firstError?.ref?.focus) {
          firstError.ref.focus()
          if (window.navigator && window.navigator.vibrate) {
            window.navigator.vibrate(50)
          }
        }
      })
    },

    async sendVerificationCode (type) {
      if (!this.validatePhoneNumber()) {
        this.showToast('请输入有效的手机号码', 'error')
        this.$nextTick(() => this.$refs.phoneInput?.focus())
        return
      }
      if (this.sendCodeAttempts >= this.maxSendCodeAttempts) {
        this.showToast('发送次数已达上限，请24小时后再试', 'error')
        return
      }
      this.isCodeSending = true
      try {
        const response = await sendVerificationCodeApi({
          countryCode: this.selectedCountryCode,
          phoneNumber: this.phoneNumber
        })
        if (response?.status === 200) {
          this.startCountdown()
          this.startCodeExpiryCountdown()
          this.sendCodeAttempts++
          this.verificationCodeExpiry = Date.now() + 5 * 60 * 1000
          this.showToast(response.message || '验证码已发送，5分钟内有效', 'success', 5000)
        } else {
          this.showToast(response?.message || '发送失败，请稍后重试', 'error')
        }
      } catch (error) {
        console.error('发送验证码错误:', error)
        this.showToast(error?.message || '网络异常，请检查连接后重试', 'error')
      } finally {
        this.isCodeSending = false
      }
    },
    startCountdown () {
      this.countdown = 60
      clearInterval(this.countdownTimer)
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },
    startCodeExpiryCountdown () {
      clearInterval(this.codeExpiryTimer)
      this.codeExpiryCountdown = 300
      this.codeExpiryTimer = setInterval(() => {
        this.codeExpiryCountdown--
        if (this.codeExpiryCountdown <= 0) {
          clearInterval(this.codeExpiryTimer)
          this.codeExpiryTimer = null
          this.verificationCodeError = '验证码已过期，请重新获取'
          this.verificationCode = ''
        }
      }, 1000)
    },

    async handleLogin () {
      if (!this.validatePhoneNumber() || !this.validatePassword()) {
        this.showToast('请检查手机号和密码格式', 'error')
        this.focusFirstError()
        return
      }
      this.isLoading = true
      try {
        const response = await passwordLogin({
          countryCode: this.selectedCountryCode,
          phoneNumber: this.phoneNumber,
          password: this.password,
          rememberMe: this.rememberMe
        })
        if (response?.status === 200 && response.token) {
          await this.login({
            token: response.token,
            user: { phone: this.phoneNumber },
            balance: response.balance || 0
          })
          this.showToast(response.message || '登录成功', 'success')
          setTimeout(() => {
            const redirect = this.$route.query.redirect
            this.$router.replace(redirect && !redirect.includes('login') ? redirect : '/')
          }, 800)
        } else {
          this.showToast(response?.message || '登录失败，请检查账号密码', 'error')
        }
      } catch (error) {
        console.error('登录错误:', error)
        this.showToast(error?.message || '登录请求超时，请稍后重试', 'error')
      } finally {
        this.isLoading = false
      }
    },
    async handleSmsLogin () {
      if (!this.validatePhoneNumber() || !this.validateVerificationCode()) {
        this.showToast('请检查手机号和验证码', 'error')
        this.focusFirstError()
        return
      }
      this.isLoading = true
      try {
        const response = await smsLogin({
          countryCode: this.selectedCountryCode,
          phoneNumber: this.phoneNumber,
          verificationCode: this.verificationCode
        })
        if (response?.status === 200 && response.token) {
          const userPhone = response.user || this.phoneNumber
          await this.login({
            token: response.token,
            user: { phone: userPhone },
            balance: response.balance || 0
          })
          this.showToast('登录成功', 'success')
          setTimeout(() => {
            const redirect = this.$route.query.redirect
            this.$router.replace(redirect && !redirect.includes('login') ? redirect : '/')
          }, 800)
        } else {
          this.showToast(response?.message || '验证码错误，请重试', 'error')
        }
      } catch (error) {
        console.error('短信登录错误:', error)
        this.showToast(error?.message || '登录请求超时，请稍后重试', 'error')
      } finally {
        this.isLoading = false
      }
    },
    async handleForgotPasswordLogin () {
      if (!this.validatePhoneNumber() || !this.validateVerificationCode() || !this.validateNewPassword()) {
        this.showToast('请完善所有必填信息', 'error')
        this.focusFirstError()
        return
      }
      this.isLoading = true
      try {
        const response = await forgotPassword({
          countryCode: this.selectedCountryCode,
          phoneNumber: this.phoneNumber,
          verificationCode: this.verificationCode,
          newPassword: this.newPassword
        })
        if (response?.status === 200 && response.token) {
          await this.login({
            token: response.token,
            user: { phone: this.phoneNumber },
            balance: 0
          })
          this.showToast('密码重置成功，已自动登录', 'success')
          setTimeout(() => {
            this.loginMethod = 'password'
          }, 1000)
        } else {
          this.showToast(response?.message || '重置失败，请检查验证码', 'error')
        }
      } catch (error) {
        console.error('重置密码错误:', error)
        this.showToast(error?.message || '请求超时，请稍后重试', 'error')
      } finally {
        this.isLoading = false
      }
    },
    async handleRegister () {
      if (!this.validatePhoneNumber() || !this.validateVerificationCode() || !this.validateNewPassword()) {
        this.showToast('请完善注册信息', 'error')
        this.focusFirstError()
        return
      }
      if (!this.agreementChecked) {
        this.showToast('请阅读并同意用户协议', 'error')
        return
      }
      this.isRegistering = true
      try {
        const response = await registerUser({
          countryCode: this.selectedCountryCode,
          phoneNumber: this.phoneNumber,
          verificationCode: this.verificationCode,
          password: this.newPassword
        })
        if (response?.status === 200 && response.token) {
          const userPhone = response.user || this.phoneNumber
          await this.login({
            token: response.token,
            user: { phone: userPhone },
            balance: response.balance || 0
          })
          this.showToast(response.message || '注册成功，欢迎加入！', 'success')
          setTimeout(() => {
            const redirect = this.$route.query.redirect
            this.$router.replace(redirect && !redirect.includes('login') ? redirect : '/')
          }, 800)
        } else {
          this.showToast(response?.message || '注册失败，手机号可能已注册', 'error')
        }
      } catch (error) {
        console.error('注册错误:', error)
        this.showToast(error?.message || '注册请求超时，请稍后重试', 'error')
      } finally {
        this.isRegistering = false
      }
    },

    goToUserAgreement () {
      const routeData = this.$router.resolve({ path: '/user-agreement' })
      window.open(routeData.href, '_blank')
    },
    goToPrivacyPolicy () {
      const routeData = this.$router.resolve({ path: '/privacy-policy' })
      window.open(routeData.href, '_blank')
    },

    resetTimers () {
      const timers = [
        this.countdownTimer,
        this.codeExpiryTimer,
        this.phoneDebounceTimer,
        this.passwordDebounceTimer,
        this.newPasswordDebounceTimer,
        this.codeDebounceTimer
      ]
      timers.forEach(timer => {
        if (timer) {
          clearInterval(timer)
          clearTimeout(timer)
        }
      })
      this.countdown = 0
      this.codeExpiryCountdown = 0
      this.verificationCodeExpiry = null
      this.toastList.forEach(t => {
        if (t.timer) clearTimeout(t.timer)
        if (t.interval) clearInterval(t.interval)
      })
      this.toastList = []
    },
    resetValidationStates () {
      this.isPhoneValidating = false
      this.isPasswordValidating = false
      this.isNewPasswordValidating = false
    }
  },
  async created () {
    try {
      const isAuthenticated = await this.checkAuth()
      if (isAuthenticated) {
        this.showToast('欢迎回来', 'success')
        const redirect = this.$route.query.redirect
        this.$router.replace(redirect && !redirect.includes('login') ? redirect : '/')
      }
    } catch (e) {
      // 未登录状态正常
    }
  },
  beforeDestroy () {
    this.resetTimers()
  }
}
</script>

<style scoped>
/* ===== 基础重置与变量 ===== */
:root {
  --primary: #4f46e5;
  --primary-dark: #3730a3;
  --accent: #7c3aed;
  --success: #059669;
  --warning: #d97706;
  --error: #dc2626;
  --gray-50: #f9fafb;
  --gray-100: #f3f4f6;
  --gray-200: #e5e7eb;
  --gray-300: #d1d5db;
  --gray-400: #9ca3af;
  --gray-500: #6b7280;
  --gray-600: #4b5563;
  --gray-700: #374151;
  --radius: 12px;
  --transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

/* ===== Toast 系统（多消息） ===== */
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
  max-width: 90vw;
  width: auto;
}
.toast-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: var(--radius);
  box-shadow: 0 8px 30px rgba(0,0,0,0.2);
  font-size: 14px;
  font-weight: 500;
  position: relative;
  overflow: hidden;
  pointer-events: auto;
  backdrop-filter: blur(8px);
  min-width: 260px;
  color: #fff;
}
.toast-success { background: linear-gradient(135deg, #10b981, #059669); }
.toast-error { background: linear-gradient(135deg, #ef4444, #dc2626); }
.toast-info { background: linear-gradient(135deg, #3b82f6, #2563eb); }
.toast-warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
.toast-icon {
  width: 24px; height: 24px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; flex-shrink: 0;
}
.toast-message { flex: 1; word-break: break-word; }
.toast-close {
  background: none; border: none; color: #fff;
  font-size: 22px; cursor: pointer; padding: 4px;
  width: 28px; height: 28px; display: flex;
  align-items: center; justify-content: center;
  border-radius: 50%; transition: background 0.2s;
}
.toast-close:hover { background: rgba(255,255,255,0.2); }
.toast-progress {
  position: absolute; bottom: 0; left: 0; height: 3px;
  background: rgba(255,255,255,0.5);
  transition: width 0.1s linear;
}
.toast-list-enter-active, .toast-list-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.toast-list-enter, .toast-list-leave-to {
  opacity: 0; transform: translateY(-12px) scale(0.95);
}

/* ===== 页面布局 ===== */
.login-register-page {
  display: flex; height: 100vh; width: 100%; min-height: 600px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
}
.left-side, .right-side { width: 50%; height: 100%; }
.left-side {
  display: flex; justify-content: center; align-items: center; padding: 40px;
}
.brand-section { text-align: center; color: #fff; max-width: 400px; }
.brand-logo {
  width: 200px; height: 200px; object-fit: cover; border-radius: 50%;
  box-shadow: 0 20px 50px rgba(0,0,0,0.3); margin-bottom: 20px;
  border: 4px solid rgba(255,255,255,0.2);
}
.brand-title { font-size: 34px; font-weight: 700; margin-bottom: 10px; }
.brand-subtitle { font-size: 15px; opacity: 0.9; }
.right-side {
  display: flex; justify-content: center; align-items: center;
  padding: 40px; overflow-y: auto; -webkit-overflow-scrolling: touch;
}
.login-register-box {
  width: 100%; max-width: 440px; background: #fff;
  border-radius: 20px; padding: 38px 34px;
  padding-bottom: calc(38px + env(safe-area-inset-bottom));
  box-shadow: 0 25px 60px rgba(0,0,0,0.25);
}

/* ===== Tab 切换 ===== */
.toggle-buttons {
  display: flex; justify-content: center; margin-bottom: 28px;
  gap: 36px; border-bottom: 2px solid var(--gray-100); padding-bottom: 14px;
}
.toggle-buttons button {
  background: none; border: none; cursor: pointer;
  color: var(--gray-500); font-size: 18px; font-weight: 600;
  padding-bottom: 10px; border-bottom: 3px solid transparent;
  transition: color var(--transition), border-color var(--transition);
  font-family: inherit;
}
.toggle-buttons button:hover { color: var(--primary-dark); }
.toggle-buttons button.active {
  color: var(--primary); border-bottom-color: var(--primary);
}
.form-content { animation: fadeIn 0.3s ease-out; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== 输入框组 ===== */
.input-group { margin-bottom: 18px; }
.phone-input { display: flex; gap: 10px; }

/* 🎯 国家选择框 - 默认就有明显深色边框 */
.country-select {
  width: 100px;
  padding: 12px 8px;
  border: 2px solid #9ca3af !important;
  border-radius: 8px;
  font-size: 14px;
  background: #fff !important;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 6px center;
  background-repeat: no-repeat;
  background-size: 14px;
  padding-right: 24px;
  color: var(--gray-700);
}
.country-select:focus {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
  outline: none;
}

.phone-number-wrapper { flex: 1; position: relative; }

/* 🎯 所有输入框 - 默认状态就有清晰可见的深色实线边框 */
input[type="tel"],
input[type="text"],
input[type="password"] {
  width: 100%;
  padding: 12px 14px;
  border: 2px solid #9ca3af !important;
  border-radius: 8px;
  font-size: 15px;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  background: #fff !important;
  color: var(--gray-700);
}

/* 聚焦时增强效果（边框变色+轻微阴影） */
input[type="tel"]:focus,
input[type="text"]:focus,
input[type="password"]:focus {
  border-color: var(--primary) !important;
  box-shadow: 0 0 0 3px rgba(79,70,229,0.12);
}

input.error {
  border-color: var(--error) !important;
}

.input-clear-btn {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--gray-400);
  font-size: 16px; padding: 4px; border-radius: 50%;
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
}
.input-clear-btn:hover { color: var(--gray-600); background: var(--gray-100); }

.password-input-wrapper { position: relative; }
.password-input-wrapper input { padding-right: 44px; }
.toggle-visibility {
  position: absolute; right: 8px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: var(--gray-400);
  padding: 6px; display: flex; align-items: center;
}
.toggle-visibility:hover { color: var(--primary-dark); }

.error-message {
  color: var(--error); font-size: 13px; margin-top: 5px; display: flex; align-items: center; gap: 4px;
}
.error-message::before { content: "⚠"; }
.input-hint { font-size: 12px; color: var(--gray-500); margin-top: 4px; display: block; }
.code-expiry-hint { font-size: 12px; color: var(--warning); margin-top: 6px; }

/* 密码强度 */
.password-strength { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12px; }
.strength-label { color: var(--gray-500); }
.strength-bar { flex: 1; height: 4px; background: var(--gray-200); border-radius: 2px; overflow: hidden; }
.strength-fill { height: 100%; border-radius: 2px; transition: width 0.3s, background 0.3s; }
.strength-fill.strength-weak { background: #ef4444; }
.strength-fill.strength-medium { background: #f59e0b; }
.strength-fill.strength-strong { background: #10b981; }
.strength-text { font-weight: 600; padding: 2px 8px; border-radius: 4px; }
.strength-text.text-weak { color: #dc2626; background: #fef2f2; }
.strength-text.text-medium { color: #d97706; background: #fffbeb; }
.strength-text.text-strong { color: #059669; background: #ecfdf5; }

/* 按钮与链接 */
.main-button {
  width: 100%; padding: 14px;
  background: linear-gradient(135deg, #4338ca 0%, #6d28d9 100%);
  color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 600;
  cursor: pointer; margin-bottom: 14px; display: flex; align-items: center; justify-content: center; gap: 8px;
  box-shadow: 0 4px 14px rgba(67,56,202,0.4);
  transition: transform 0.2s, box-shadow 0.2s;
}
.main-button:disabled {
  opacity: 0.6; cursor: not-allowed;
  background: linear-gradient(135deg, #6ba2f9, #1b64f5);
  box-shadow: none;
}
.main-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(67,56,202,0.55);
}
.loading-spinner {
  width: 18px; height: 18px; border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff; border-radius: 50%; animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.verification-code { display: flex; gap: 10px; }
.code-button {
  width: 110px; padding: 12px 8px;
  background: linear-gradient(135deg, #4338ca 0%, #6d28d9 100%);
  color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600;
  cursor: pointer; white-space: nowrap;
}
.code-button:disabled {
  opacity: 0.6; background: #5395f7; cursor: not-allowed;
}
.link-text { color: var(--primary); cursor: pointer; font-weight: 500; }
.link-text:hover { text-decoration: underline; }
.additional-options { display: flex; justify-content: space-between; margin-top: 12px; font-size: 14px; }

/* ===== 协议复选框样式 ===== */
.agreement { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; margin-bottom: 20px; }
.checkbox-container {
  position: relative;
  cursor: pointer;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
.checkbox-container input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  z-index: 2;
}
/* 复选框边框：默认深色实线，确保未选中也清晰可见 */
.custom-checkmark {
  display: block;
  width: 18px;
  height: 18px;
  border: 2px solid #9ca3af !important;
  border-radius: 4px;
  position: relative;
  background: #fff !important;
  transition: all 0.2s;
}
.checkbox-container input:checked ~ .custom-checkmark {
  background: linear-gradient(135deg, #4338ca, #6d28d9);
  border-color: #4338ca !important;
}
/* 🔧 修复：勾选后显示对号 - 改用 Unicode 字符 + 居中定位 */
.custom-checkmark::after {
  content: "✓";
  display: none;
  color: rgb(14, 30, 246);
  font-size: 14px;
  font-weight: bold;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: 1;
}
.checkbox-container input:checked ~ .custom-checkmark::after {
  display: block;
}
.agreement-text {
  color: var(--gray-600);
  flex: 1;
  line-height: 1.5;
}
.login-link { text-align: center; margin-top: 18px; font-size: 14px; color: var(--gray-500); }

/* 记住我复选框样式 */
.remember-me { margin-bottom: 16px; }
.checkbox-label-wrapper {
  display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 14px; color: var(--gray-600);
}
.checkbox-label-wrapper input[type="checkbox"] {
  width: 16px; height: 16px;
  border: 2px solid #9ca3af !important;
  border-radius: 4px;
  cursor: pointer;
  accent-color: var(--primary);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .login-register-page { flex-direction: column; height: auto; min-height: 100vh; overflow-y: auto; }
  .left-side, .right-side { width: 100%; height: auto; }
  .left-side { padding: 30px 20px; }
  .brand-logo { width: 120px; height: 120px; }
  .brand-title { font-size: 24px; }
  .right-side { padding: 20px; align-items: flex-start; }
  .login-register-box { padding: 28px 20px; }
}
</style>
